// @ts-check

import React from "react";
import { createGuid } from "./cryptoHelper";
import { mkDefaultRow, defaultValues, mkTreeViewChild, defaultValues0Rows, computeValuesHdph, mkDefaultRowGeneric } from "./createTable";

export const createLOCATable = (drillholeName, project) => {
  // 生成唯一ID (the unique id of the LOCA table)
  const newDrillId = createGuid();
  // the name of the new drillhole
  const newLocaId = drillholeName;

  /**
   * 生成符合RichTreeView期望結構的新鑽孔物件
   *
   * @type {{
   *  id: string;
   *  label: string;
   *  children: Array<{
   *    id: string;
   *    label: string;
   *    data?: any;  // not actually used
   *  }>
   * }}
   */
  const newDrillHole = {
    id: newDrillId,
    label: newLocaId,
    children: [
      {
        id: `${newDrillId}|${newLocaId}|LOCA`,
        label: "LOCA",
        data: {
          drillId: newDrillId,
          table: newLocaId,
          type: "LOCA",
        },
      },
    ],
  };

  // 生成tabLabel
  const tabLabel = (
    <>
      {newLocaId}
      <br />
      {"LOCA"}
      {newDrillId}
    </>
  );

  // 根據圖片更新defaultColumns
  const defaultColumns = [
    { field: "id", headerName: "ID", autoWidth: true, editable: false },
    {
      field: "LOCA_ID",
      headerName: "LOCA_ID",
      autoWidth: true,
      editable: true,
    },
    {
      field: "project_id",
      headerName: "Project ID",
      autoWidth: true,
      editable: false,
    },
    {
      field: "LOCA_TYPE",
      headerName: "LOCA_TYPE",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_AS_NATE",
      headerName: "LOCA_AS_NATE",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_AS_NATN",
      headerName: "LOCA_AS_NATN",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_NATE",
      headerName: "LOCA_NATE",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_NATN",
      headerName: "LOCA_NATN",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_GREF",
      headerName: "LOCA_GREF",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_GL",
      headerName: "LOCA_GL",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_AS_GL",
      headerName: "LOCA_AS_GL",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_REM",
      headerName: "LOCA_REM",
      autoWidth: true,
      editable: true,
    },
    {
      field: "drilling_type",
      headerName: "Drilling Type",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_STAR",
      headerName: "LOCA_STAR",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_ENDD",
      headerName: "LOCA_ENDD",
      autoWidth: true,
      editable: true,
    },
    // 第二張圖片欄位
    {
      field: "LOCA_STAT",
      headerName: "LOCA_STAT",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_FDEP",
      headerName: "LOCA_FDEP",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_PURP",
      headerName: "LOCA_PURP",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_TERM",
      headerName: "LOCA_TERM",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_LETT",
      headerName: "LOCA_LETT",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_LOCX",
      headerName: "LOCA_LOCX",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_LOCY",
      headerName: "LOCA_LOCY",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_LOCZ",
      headerName: "LOCA_LOCZ",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_LREF",
      headerName: "LOCA_LREF",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_DATM",
      headerName: "LOCA_DATM",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_ETRV",
      headerName: "LOCA_ETRV",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_NTRV",
      headerName: "LOCA_NTRV",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_LTRV",
      headerName: "LOCA_LTRV",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_XTRL",
      headerName: "LOCA_XTRL",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_YTRL",
      headerName: "LOCA_YTRL",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_ZTRL",
      headerName: "LOCA_ZTRL",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_LAT",
      headerName: "LOCA_LAT",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_LON",
      headerName: "LOCA_LON",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_ELAT",
      headerName: "LOCA_ELAT",
      autoWidth: true,
      editable: true,
    },
    // 第三張圖片欄位
    {
      field: "LOCA_ELON",
      headerName: "LOCA_ELON",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_LLZ",
      headerName: "LOCA_LLZ",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_LOCM",
      headerName: "LOCA_LOCM",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_LOCA",
      headerName: "LOCA_LOCA",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_CLST",
      headerName: "LOCA_CLST",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_ALID",
      headerName: "LOCA_ALID",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_OFFS",
      headerName: "LOCA_OFFS",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_CNGE",
      headerName: "LOCA_CNGE",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_TRAN",
      headerName: "LOCA_TRAN",
      autoWidth: true,
      editable: true,
    },
    {
      field: "FILE_FSET",
      headerName: "FILE_FSET",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_CKBY",
      headerName: "LOCA_CKBY",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_CKDT",
      headerName: "LOCA_CKDT",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_NATD",
      headerName: "LOCA_NATD",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_ORID",
      headerName: "LOCA_ORID",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_ORJO",
      headerName: "LOCA_ORJO",
      autoWidth: true,
      editable: true,
    },
    {
      field: "LOCA_ORCO",
      headerName: "LOCA_ORCO",
      autoWidth: true,
      editable: true,
    },
    { field: "active", headerName: "Active", autoWidth: true, editable: true },
    // 時間戳欄位
    {
      field: "created_at",
      headerName: "Created At",
      autoWidth: true,
      editable: false,
    },
    {
      field: "modified_at",
      headerName: "Modified At",
      autoWidth: true,
      editable: false,
    },
  ];

  // 根據圖片更新defaultRow
  const defaultRow = {
    id: newDrillId,
    LOCA_ID: newLocaId,
    project_id: project.project_id,
    LOCA_TYPE: "",
    LOCA_AS_NATE: 0,
    LOCA_AS_NATN: 0,
    LOCA_NATE: 0,
    LOCA_NATN: 0,
    LOCA_GREF: "",
    LOCA_GL: 0,
    LOCA_AS_GL: 0,
    LOCA_REM: "",
    drilling_type: "",
    LOCA_STAR: "",
    LOCA_ENDD: "",
    LOCA_STAT: "",
    LOCA_FDEP: 0,
    LOCA_PURP: "",
    LOCA_TERM: "",
    LOCA_LETT: "",
    LOCA_LOCX: 0,
    LOCA_LOCY: 0,
    LOCA_LOCZ: 0,
    LOCA_LREF: "",
    LOCA_DATM: "",
    LOCA_ETRV: 0,
    LOCA_NTRV: 0,
    LOCA_LTRV: 0,
    LOCA_XTRL: 0,
    LOCA_YTRL: 0,
    LOCA_ZTRL: 0,
    LOCA_LAT: 0,
    LOCA_LON: 0,
    LOCA_ELAT: 0,
    LOCA_ELON: 0,
    LOCA_LLZ: "",
    LOCA_LOCM: "",
    LOCA_LOCA: "",
    LOCA_CLST: "",
    LOCA_ALID: "",
    LOCA_OFFS: 0,
    LOCA_CNGE: "",
    LOCA_TRAN: "",
    FILE_FSET: "",
    LOCA_CKBY: "",
    LOCA_CKDT: "",
    LOCA_NATD: "",
    LOCA_ORID: "",
    LOCA_ORJO: "",
    LOCA_ORCO: "",
    active: 1,
    created_at: new Date().toISOString(),
    modified_at: new Date().toISOString(),
  };

  const extra = {}
  for (const key in defaultValues) {
    // this is safe because the key is from defaultValues
    const tableName = /** @type {import("./createTable").TableName} */ (key);

    const child = mkTreeViewChild(newDrillId, newLocaId, tableName)
    newDrillHole.children.push(child)

    const row = mkDefaultRow(tableName, newDrillId, newLocaId)
    if (tableName === 'HORN') {
      extra[tableName.toLowerCase()] = row
    } else {
      extra[tableName.toLowerCase()] = [row]
    }
  }

  // these tables are created with 0 rows initially
  for (const key in defaultValues0Rows) {
    // this is safe because the key is from defaultValues0Rows
    const tableName = /** @type {import("./createTable").TableName0Rows} */ (key);

    const child = mkTreeViewChild(newDrillId, newLocaId, tableName)
    newDrillHole.children.push(child)

    extra[tableName.toLowerCase()] = []
  }

  // as we've added the hdph row above, we can now add tables that require the hdph row
  // to compute default values
  const hdph = extra['hdph'][0]

  for (const key in computeValuesHdph) {
    const tableName = /** @type {import("./createTable").TablesNeedingHdph} */ (key);

    const child = mkTreeViewChild(newDrillId, newLocaId, tableName)
    newDrillHole.children.push(child)

    // these tables compute all values in the compute function and not default array
    const defaults = []
    const compute = computeValuesHdph[tableName]
    const row = mkDefaultRowGeneric(newDrillId, newLocaId, defaults, compute, hdph)
    extra[tableName.toLowerCase()] = [row]
  }

  return {
    // rich tree view item
    drillHole: newDrillHole,
    tabLabel,
    columns: defaultColumns,

    // row can be used for setCurrentData
    row: defaultRow,

    // caller should copy these values to defaultRow for syncDrillhole
    extra,

    // these are not used
    drillId: newDrillId,
    locaId: newLocaId,
  };
};
